import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  PLATFORM_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { appRoutes } from './app.routes';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import { provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { environment } from '../environments/environment';
import {
  API_BASE_URL,
  AuthService,
  AuthOtpService,
  RUNTIME_CONFIG,
  GlobalPartnerStore,
  SupplierService,
  secureApiInterceptor,
  authErrorInterceptor,
  languageHeaderInterceptor,
  PARTNER_NAVBAR_CONFIG,
} from '@ichoosr-core/partner/domain';
import { RuntimeConfigService } from '../runtime-config.service';
import { enableSentryLogging, provideSentry } from '@ichoosr-core/logging';
import { catchError, firstValueFrom, of } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { version } from '../version.json';
import clarity from '@microsoft/clarity';
import {
  AnalyticsService,
  NavigationHandlerService,
  AnalyticsErrorHandler,
} from '@ichoosr-core/partner/partner-analytics';

const appConfig: ApplicationConfig = {
  providers: [
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({
        filter: (req) => {
          const shouldFilter = req.url.startsWith(`${environment.apiBaseUrl}/v1`);

          return !shouldFilter;
        },
      }),
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withComponentInputBinding()),
    provideHttpClient(
      withFetch(),
      withInterceptorsFromDi(),
      withInterceptors([secureApiInterceptor, authErrorInterceptor, languageHeaderInterceptor]),
    ),
    provideSentry(),
    {
      provide: API_BASE_URL,
      useValue: environment.apiBaseUrl,
    },
    {
      provide: RUNTIME_CONFIG,
      useExisting: RuntimeConfigService,
    },
    {
      provide: AuthService,
      useClass: AuthOtpService,
    },
    // App initializer for Sentry
    provideAppInitializer(async () => {
      const runtimeConfig = inject(RuntimeConfigService);
      await runtimeConfig.loadConfig();

      // We must inject this service to ensure it runs on the server
      // to get environment variables and transfer them to the client
      enableSentryLogging({
        applicationName: 'ng-partner-shell',
        dsn: runtimeConfig.LOGGING_SENTRY_DSN,
        sourceVersion: runtimeConfig.SOURCE_VERSION,
        version: version,
      });
    }),

    // App initializer for Microsoft Clarity
    provideAppInitializer(async () => {
      const platformId = inject(PLATFORM_ID);
      const runtimeConfig = inject(RuntimeConfigService);
      await runtimeConfig.loadConfig();

      if (!isPlatformBrowser(platformId)) {
        return;
      }

      const clarityProjectId = runtimeConfig.CLARITY_PROJECT_ID;

      if (environment.clarity.enabled && clarityProjectId) {
        clarity.init(clarityProjectId);
      }
    }),

    // App initializer for analytics toggles
    provideAppInitializer(async () => {
      const runtimeConfig = inject(RuntimeConfigService);
      const analyticsService = inject(AnalyticsService);

      await runtimeConfig.loadConfig();
      analyticsService.configure({
        consoleLogging: runtimeConfig.ANALYTICS_CONSOLE_LOGGING,
      });
    }),

    // App initializer for global store and navigation handler
    provideAppInitializer(async () => {
      const platformId = inject(PLATFORM_ID);
      if (!isPlatformBrowser(platformId)) {
        return;
      }

      const supplierService = inject(SupplierService);
      const authService = inject(AuthService);
      const globalStore = inject(GlobalPartnerStore);
      const navigationHandlerService = inject(NavigationHandlerService);

      const isLoggedIn = await firstValueFrom(authService.isLoggedIn());

      if (isLoggedIn) {
        const data = await firstValueFrom(supplierService.getSupplierData().pipe(catchError(() => of(null))));
        if (data?.countryCode) {
          globalStore.setCountry(data.countryCode);
          globalStore.setInstallerInfo(data.id, data.name);
        }
      }

      navigationHandlerService.handle();
    }),

    // Add analytics global event handler
    {
      provide: ErrorHandler,
      useClass: AnalyticsErrorHandler,
    },

    {
      provide: PARTNER_NAVBAR_CONFIG,
      useValue: { localizeHomeLink: true, production: environment.production, useOtpUserInfo: true },
    },
  ],
};
export const appConfigFactory = (): ApplicationConfig => {
  return appConfig;
};
